import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../services/api';

interface Category {
  id: string;
  name: string;
}

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategory, setNewCategory] = useState<Category>({ id: '', name: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch categories from API on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const response = await categoriesAPI.getAll();
      if (response?.data?.data) {
        setCategories(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      setErrorMessage(error.response?.data?.error?.message || 'Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!newCategory.id || !newCategory.name) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    // Check if ID already exists
    if (categories.some(cat => cat.id === newCategory.id) && !isEditing) {
      setErrorMessage('Category ID already exists');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        id: newCategory.id.trim().toLowerCase(),
        name: newCategory.name.trim()
      };

      if (isEditing && editId) {
        await categoriesAPI.update(editId, payload);
        setCategories(categories.map(cat => 
          cat.id === editId ? payload : cat
        ));
        setIsEditing(false);
        setEditId(null);
      } else {
        await categoriesAPI.create(payload);
        setCategories([...categories, payload]);
      }

      // Reset form
      setNewCategory({ id: '', name: '' });
    } catch (error: any) {
      console.error('Error saving category:', error);
      setErrorMessage(error.response?.data?.error?.message || 'Failed to save category');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category: Category) => {
    setNewCategory(category);
    setIsEditing(true);
    setEditId(category.id);
    setErrorMessage(null);
  };

  const handleDeleteCategory = async (id: string) => {
    if (id === 'all') {
      setErrorMessage('Cannot delete the "All Products" category');
      return;
    }

    if (window.confirm('Are you sure you want to delete this category?')) {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        await categoriesAPI.delete(id);
        setCategories(categories.filter(cat => cat.id !== id));
      } catch (error: any) {
        console.error('Error deleting category:', error);
        setErrorMessage(error.response?.data?.error?.message || 'Failed to delete category');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setNewCategory({ id: '', name: '' });
    setIsEditing(false);
    setEditId(null);
    setErrorMessage(null);
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-6">
        <h2 className="text-xl font-heading font-bold text-text mb-6">Category Management</h2>
        
        {errorMessage && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            <p>{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleAddCategory} className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
                Category ID *
              </label>
              <input
                type="text"
                id="categoryId"
                placeholder="e.g. rods, reels"
                value={newCategory.id}
                onChange={(e) => setNewCategory({...newCategory, id: e.target.value.toLowerCase().trim()})}
                disabled={isLoading || (isEditing && editId === 'all')}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:bg-gray-100 disabled:text-gray-500"
              />
              <p className="mt-1 text-xs text-gray-500">Use a simple identifier without spaces</p>
            </div>
            
            <div>
              <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 mb-1">
                Category Name *
              </label>
              <input
                type="text"
                id="categoryName"
                placeholder="e.g. Fishing Rods"
                value={newCategory.name}
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                disabled={isLoading}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <p className="mt-1 text-xs text-gray-500">Displayed to customers on the website</p>
            </div>
          </div>
          
          <div className="mt-4 flex space-x-3">
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors flex items-center ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEditing ? 'Updating...' : 'Adding...'}
                </>
              ) : isEditing ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Update Category
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Category
                </>
              )}
            </button>
            
            {isEditing && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={isLoading}
                className={`px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors ${
                  isLoading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
        
        <div className="border-t border-gray-200 pt-6">
          <h3 className="font-heading font-semibold text-gray-700 mb-4">Existing Categories</h3>
          
          {/* Loading State */}
          {isLoading && categories.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600">Loading categories...</p>
            </div>
          )}
          
          {!isLoading && categories.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{category.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                          disabled={isLoading}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className={`text-red-600 hover:text-red-900 ${category.id === 'all' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isLoading || category.id === 'all'}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !isLoading && categories.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="text-lg font-heading font-bold text-text mb-2">No Categories Found</h3>
              <p className="text-gray-500">Add a new category to get started</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CategoryManager; 